import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box } from '@mui/material';
import alertImage from 'assets/img/alert.svg';
import { CURRENCY_FORMATTER, PRODUCT_INVALID_VALUE } from 'common/constants';
import {
  ACTIVE_PRODUCT_MESSAGE,
  DEACTIVE_PRODUCT_MESSAGE,
} from 'common/constants/changeProductAvailabilityMessage.constants';
import { formatCurrency } from 'common/constants/currency';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setProductSelected, setProductValues } from 'v3/data/store/product/actions';
import { IProduct } from 'v3/data/store/product/types';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { UnsupportedValueCard } from 'v3/pages/Products/List/styles';
import { changeProductPrice } from 'v3/pages/Products/List/useCases/change-product-price';
import * as api from 'v3/services/api';
import { updateProduct } from 'v3/services/api';
import { ChangeValueModal } from 'v3/shared/components/Modal/ChangePriceModal';
import { ConfirmationModal } from 'v3/shared/components/Modal/ConfirmationModal';
import { SwitchCommon } from 'v3/shared/components/Switch';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { LabelSmall } from 'v3/shared/typography/LabelSmall';
import {
  AlignClientInfo,
  AlignSwitch,
  AlignSwitchContainer,
  AlignValues,
  BodyLine,
  DeviceImg,
  HeaderLine,
  MainContainer,
  SubtitleLine,
  TableCellInRow,
} from '../styles';

interface ProductMobileTableForStoreProps {
  items: IProduct[];
  shouldDisableAvailability: (product: IProduct) => boolean;
}

export const ProductMobileTableForStore: React.FC<ProductMobileTableForStoreProps> = ({
  items,
  shouldDisableAvailability,
}) => {
  const [changePriceModalOpen, setChangePriceModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState<boolean>(false);

  const { productSelected } = useStoreSelector((store) => store.products);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_3074_IMPLEMENT_MAXIMUM_VALUES);
    if (response.data.enabled) {
      setSellerFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  const handleSwitchChange = (product: IProduct, event: React.ChangeEvent<HTMLInputElement>) => {
    const switchNewValue = Boolean(event.target.checked);
    setProductSelected({ ...product, available: switchNewValue });
    const currentSubtitle = switchNewValue ? ACTIVE_PRODUCT_MESSAGE : DEACTIVE_PRODUCT_MESSAGE;
    setSubtitle(currentSubtitle);
    setConfirmationModalOpen(true);
  };

  const handleOpenEditPriceModal = (product: IProduct) => {
    setProductSelected(product);
    setChangePriceModalOpen(true);
  };

  const changePriceHandler = async (storePrice: number) => {
    await changeProductPrice(productSelected.id, Number(storePrice) / CURRENCY_FORMATTER, productSelected.available);
  };

  const handleConfirm = async () => {
    try {
      const response = await updateProduct(productSelected.id, { available: productSelected.available });
      setProductValues(response.data);

      response.data.available
        ? showSnackbar('O produto está ativo no catálogo', 'success', true)
        : showSnackbar('O produto foi desativado do catálogo', 'success', true);
    } catch (error) {
      showErrorDialog('Erro ao editar disponibilidade do produto.', error);
    } finally {
      setConfirmationModalOpen(false);
    }
  };

  return (
    <Fragment>
      {items.map((item) => (
        <MainContainer key={item.id}>
          <AlignValues>
            <HeaderLine>Modelo</HeaderLine>
            <AlignClientInfo>
              <DeviceImg src={item.sku.image.marketingUrl} alt={item.id} />
              <BodyLine>{item.sku.product.name}</BodyLine>
              <SubtitleLine>{`${item.sku.storage}GB`}</SubtitleLine>
            </AlignClientInfo>
          </AlignValues>

          <AlignValues>
            <HeaderLine>Preço</HeaderLine>
            <AlignClientInfo>
              <TableCellInRow>
                <Box>
                  <BodyLine>{formatCurrency(item.storePrice)}</BodyLine>
                  {item.storePrice <= PRODUCT_INVALID_VALUE && (
                    <UnsupportedValueCard>
                      <LabelSmall>Necessário informar o preço</LabelSmall>
                    </UnsupportedValueCard>
                  )}
                  {sellerFeatureEnabled && item.storePrice > item.sku.maxPrice && (
                    <UnsupportedValueCard>
                      <LabelSmall>
                        Valor máximo do produto deve ser {formatCurrency(parseFloat(item.sku.maxPrice.toFixed(2)))}
                      </LabelSmall>
                    </UnsupportedValueCard>
                  )}
                </Box>

                <EditOutlinedIcon onClick={() => handleOpenEditPriceModal(item)} />
              </TableCellInRow>
            </AlignClientInfo>
          </AlignValues>

          <AlignSwitchContainer>
            <HeaderLine>Mostrar no Catálogo</HeaderLine>
            <AlignSwitch>
              <SwitchCommon
                disabled={sellerFeatureEnabled ? shouldDisableAvailability(item) : false}
                checked={item.available}
                onChange={(event) => {
                  handleSwitchChange(item, event);
                }}
              />
            </AlignSwitch>
          </AlignSwitchContainer>

          <ChangeValueModal
            modal={{
              initialPrice: productSelected.storePrice,
              active: changePriceModalOpen,
              title: 'Editar Preço',
              activeButton: true,
              cancelButton: 'Cancelar',
              saveButton: 'Salvar',
              maxPrice: productSelected.sku.maxPrice,
            }}
            onClose={() => setChangePriceModalOpen(false)}
            onConfirm={changePriceHandler}
          />

          <ConfirmationModal
            modal={{
              active: confirmationModalOpen,
              title: 'Você tem certeza?',
              subtitle: subtitle,
              image: alertImage,
              cancelButtonText: 'Cancelar',
              confirmButtonText: 'Sim, desejo continuar',
            }}
            onClose={() => setConfirmationModalOpen(false)}
            onConfirm={handleConfirm}
          />
        </MainContainer>
      ))}
    </Fragment>
  );
};
